import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Sparkles, Plus, Trash2, Edit3, Check, X, PowerOff, Power, Loader2,
    Paperclip, FileText,
} from "lucide-react";

const DATE_FMT = new Intl.DateTimeFormat("da-DK", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

const BRAND_OPTIONS = [
    { value: "both", label: "Begge brands" },
    { value: "gardinbussen", label: "Gardinbussen" },
    { value: "gardinfabrikken", label: "Gardinfabrikken" },
];

// Convert the brands array stored on the backend into the UI's single value.
const brandsToOption = (brands) => {
    if (!brands || brands.length === 0) return "both";
    if (brands.length === 1) return brands[0];
    return "both";
};

const optionToBrands = (option) => {
    if (option === "both" || !option) return [];
    return [option];
};

const BRAND_BADGE = {
    gardinbussen: { label: "Gardinbussen", cls: "bg-[#2C2A29] text-white" },
    gardinfabrikken: { label: "Gardinfabrikken", cls: "bg-[#39B5B5] text-white" },
};

function BrandSelector({ value, onChange, idPrefix, disabled }) {
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={`${idPrefix}-brand-selector`}>
            {BRAND_OPTIONS.map((opt) => {
                const active = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        disabled={disabled}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                            active
                                ? "bg-[#4A4641] text-white border-[#4A4641]"
                                : "bg-white text-[#5C5854] border-[#E5E1DA] hover:border-[#4A4641]"
                        } disabled:opacity-50`}
                        data-testid={`${idPrefix}-brand-${opt.value}`}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function AttachmentList({ files, onRemove, disabled, idPrefix }) {
    if (!files || files.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${idPrefix}-attachment-list`}>
            {files.map((f) => (
                <div
                    key={f.id}
                    className="flex items-center gap-1.5 bg-white border border-[#E5E1DA] rounded px-2 py-1 text-xs text-[#2C2A29]"
                    data-testid={`${idPrefix}-attachment-${f.id}`}
                    title={f.filename}
                >
                    <FileText className="w-3 h-3 text-[#78736E]" />
                    <span className="max-w-[160px] truncate">{f.filename}</span>
                    {onRemove && !disabled && (
                        <button
                            type="button"
                            onClick={() => onRemove(f.id)}
                            className="text-[#78736E] hover:text-[#C2410C]"
                            aria-label="Fjern fil"
                            data-testid={`${idPrefix}-attachment-remove-${f.id}`}
                        >
                            <X className="w-3 h-3" />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

async function uploadInstructionFile(file) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/public/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return {
        id: data.id,
        filename: data.filename,
        content_type: data.content_type,
        size: data.size,
    };
}

export default function AIInstructions() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newText, setNewText] = useState("");
    const [newBrand, setNewBrand] = useState("both");
    const [newFiles, setNewFiles] = useState([]); // [{id, filename, content_type, size}]
    const [newUploading, setNewUploading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const [editBrand, setEditBrand] = useState("both");
    const [editFiles, setEditFiles] = useState([]);
    const [editUploading, setEditUploading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const newFileInputRef = useRef(null);
    const editFileInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/ai-instructions");
            const base = data.items || [];
            // Hydrate attachment filenames in parallel so view-mode shows real names
            const allFids = Array.from(new Set(base.flatMap((i) => i.file_ids || [])));
            const metaMap = {};
            if (allFids.length) {
                await Promise.all(
                    allFids.map(async (fid) => {
                        try {
                            const { data: m } = await api.get(`/files/${fid}/meta`);
                            metaMap[fid] = {
                                id: fid,
                                filename: m.original_filename,
                                content_type: m.content_type,
                                size: m.size,
                            };
                        } catch {
                            metaMap[fid] = { id: fid, filename: "Vedhæftet fil" };
                        }
                    })
                );
            }
            const enriched = base.map((i) => ({
                ...i,
                _file_meta: (i.file_ids || []).map((fid) => metaMap[fid] || { id: fid, filename: "Vedhæftet fil" }),
            }));
            setItems(enriched);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke hente instrukser");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Build filename lookup from items so we can display attachments without an extra round-trip
    useEffect(() => {
        // Hydrate cache from the full items — we store filename on the item itself only after first upload.
        // For items created in a previous session, backend sends file_ids but no filename. We'll fetch meta
        // lazily when the user opens edit mode. For view mode we display a generic "Vedhæftet fil" marker.
    }, [items]);

    const canEdit = (item) => isAdmin || item.author_id === user?.id;

    const handleUpload = async (fileList, slot) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const setBusy = slot === "new" ? setNewUploading : setEditUploading;
        const setList = slot === "new" ? setNewFiles : setEditFiles;
        setBusy(true);
        try {
            for (const file of files) {
                if (file.size > 25 * 1024 * 1024) {
                    toast.error(`${file.name} er for stor (max 25 MB)`);
                    continue;
                }
                try {
                    const meta = await uploadInstructionFile(file);
                    setList((prev) => [...prev, meta]);
                } catch (e) {
                    toast.error(e.response?.data?.detail || `Kunne ikke uploade ${file.name}`);
                }
            }
        } finally {
            setBusy(false);
        }
    };

    const create = async () => {
        const text = newText.trim();
        if (!text) return;
        setCreating(true);
        try {
            const payload = {
                text,
                brands: optionToBrands(newBrand),
                file_ids: newFiles.map((f) => f.id),
            };
            const { data } = await api.post("/ai-instructions", payload);
            // Preserve the filenames locally so view mode can show them without an extra fetch
            const enriched = { ...data, _file_meta: newFiles };
            setItems((prev) => [enriched, ...prev]);
            setNewText("");
            setNewBrand("both");
            setNewFiles([]);
            toast.success("Instruks tilføjet");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke tilføje");
        } finally {
            setCreating(false);
        }
    };

    const startEdit = async (item) => {
        setEditingId(item.id);
        setEditText(item.text);
        setEditBrand(brandsToOption(item.brands));
        const fileIds = item.file_ids || [];
        if (fileIds.length) {
            // If we already have meta on the item, use it; otherwise fetch from backend
            if (item._file_meta && item._file_meta.length === fileIds.length) {
                setEditFiles(item._file_meta);
            } else {
                try {
                    const metas = await Promise.all(
                        fileIds.map(async (fid) => {
                            const { data } = await api.get(`/files/${fid}/meta`);
                            return {
                                id: fid,
                                filename: data.original_filename,
                                content_type: data.content_type,
                                size: data.size,
                            };
                        })
                    );
                    setEditFiles(metas);
                } catch (e) {
                    setEditFiles(fileIds.map((fid) => ({ id: fid, filename: "Vedhæftet fil", content_type: "" })));
                }
            }
        } else {
            setEditFiles([]);
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditText("");
        setEditBrand("both");
        setEditFiles([]);
    };

    const saveEdit = async (id) => {
        const text = editText.trim();
        if (!text) return;
        setBusyId(id);
        try {
            const payload = {
                text,
                brands: optionToBrands(editBrand),
                file_ids: editFiles.map((f) => f.id),
            };
            const { data } = await api.patch(`/ai-instructions/${id}`, payload);
            const enriched = { ...data, _file_meta: editFiles };
            setItems((prev) => prev.map((i) => (i.id === id ? enriched : i)));
            cancelEdit();
            toast.success("Instruks opdateret");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke gemme");
        } finally {
            setBusyId(null);
        }
    };

    const toggleActive = async (item) => {
        setBusyId(item.id);
        try {
            const { data } = await api.patch(`/ai-instructions/${item.id}`, { is_active: !item.is_active });
            setItems((prev) => prev.map((i) => (i.id === item.id ? { ...data, _file_meta: i._file_meta } : i)));
            toast.success(data.is_active ? "Instruks aktiveret" : "Instruks pauset");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke ændre status");
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (id) => {
        if (!confirm("Slet denne instruks permanent?")) return;
        setBusyId(id);
        try {
            await api.delete(`/ai-instructions/${id}`);
            setItems((prev) => prev.filter((i) => i.id !== id));
            toast.success("Instruks slettet");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kunne ikke slette");
        } finally {
            setBusyId(null);
        }
    };

    const activeCount = items.filter((i) => i.is_active).length;

    return (
        <div className="max-w-3xl" data-testid="ai-instructions-page">
            <div className="flex items-start gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-[#F0EBE1] flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="w-5 h-5 text-[#4A4641]" />
                </div>
                <div>
                    <h1 className="font-heading text-2xl font-medium text-[#2C2A29]">AI-instrukser</h1>
                    <p className="text-sm text-[#78736E] mt-1 max-w-2xl">
                        Løbende logbog over specifikke retningslinjer som AI'en tager i betragtning når den foreslår svar til kunder. Handleplanen følges altid — det her er finjusteringer og læringer efterhånden som I bruger AI'en. Kun aktive instrukser sendes til AI, og kun hvis brand matcher sagen.
                    </p>
                    <div className="text-xs text-[#78736E] mt-2">
                        <span className="font-semibold text-[#4A4641]">{activeCount}</span> aktive · {items.length - activeCount} pauset · {items.length} i alt
                    </div>
                </div>
            </div>

            {/* Add new */}
            <div className="bg-white border border-[#E5E1DA] rounded-xl p-5 mb-6" data-testid="new-instruction-card">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#78736E] mb-2">Tilføj ny instruks</div>
                <Textarea
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="Fx: Ved 'Produktion godkendt' skal vi altid nævne 4 ugers leveringstid."
                    rows={3}
                    maxLength={1500}
                    className="border-[#E5E1DA] text-sm resize-y"
                    data-testid="new-instruction-textarea"
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) create(); }}
                />
                <div className="mt-3 space-y-2">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#78736E] mb-1.5">Gælder for</div>
                        <BrandSelector value={newBrand} onChange={setNewBrand} idPrefix="new" disabled={creating} />
                    </div>
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#78736E] mb-1.5">
                            Vedhæftede filer <span className="normal-case font-normal text-[#A19E9A]">— sendes automatisk med når AI anvender denne instruks</span>
                        </div>
                        <input
                            ref={newFileInputRef}
                            type="file"
                            multiple
                            accept="image/*,.pdf,.heic,.heif,.doc,.docx,.xls,.xlsx"
                            className="hidden"
                            data-testid="new-instruction-file-input"
                            onChange={(e) => {
                                handleUpload(e.target.files, "new");
                                e.target.value = "";
                            }}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => newFileInputRef.current?.click()}
                            disabled={newUploading || creating}
                            className="h-8 border-dashed text-xs"
                            data-testid="new-instruction-upload-button"
                        >
                            {newUploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5 mr-1.5" />}
                            Vedhæft fil
                        </Button>
                        <AttachmentList
                            files={newFiles}
                            onRemove={(fid) => setNewFiles((prev) => prev.filter((f) => f.id !== fid))}
                            disabled={creating}
                            idPrefix="new-instruction"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                    <div className="text-xs text-[#78736E]">{newText.length}/1500 tegn · Ctrl+Enter for at tilføje</div>
                    <Button
                        onClick={create}
                        disabled={creating || newUploading || !newText.trim()}
                        className="bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                        data-testid="add-instruction-button"
                    >
                        {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                        Tilføj instruks
                    </Button>
                </div>
            </div>

            {/* Log */}
            <div className="space-y-3" data-testid="instructions-log">
                {loading && (
                    <div className="text-center text-sm text-[#78736E] py-8">Indlæser...</div>
                )}
                {!loading && items.length === 0 && (
                    <div className="text-center text-sm text-[#78736E] py-8 border border-dashed border-[#E5E1DA] rounded-xl">
                        Ingen instrukser endnu. Tilføj den første ovenover.
                    </div>
                )}
                {items.map((item) => {
                    const editable = canEdit(item);
                    const isEditing = editingId === item.id;
                    const brands = item.brands || [];
                    const brandScope = brands.length === 0 ? "both" : brands;
                    const files = item._file_meta || (item.file_ids || []).map((fid) => ({ id: fid, filename: "Vedhæftet fil" }));
                    return (
                        <div
                            key={item.id}
                            className={`bg-white border rounded-xl p-4 ${item.is_active ? "border-[#E5E1DA]" : "border-[#E5E1DA] bg-[#FAF9F7] opacity-60"}`}
                            data-testid={`instruction-item-${item.id}`}
                        >
                            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                                <div className="text-xs text-[#78736E] flex items-center flex-wrap gap-x-1.5 gap-y-1">
                                    <span className="font-semibold text-[#4A4641]">{item.author_name}</span>
                                    <span>·</span>
                                    <span>{DATE_FMT.format(new Date(item.created_at))}</span>
                                    {!item.is_active && (
                                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[10px] font-semibold uppercase tracking-wider text-[#78736E]">
                                            Pauset
                                        </span>
                                    )}
                                    {brandScope === "both" ? (
                                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#F4F2EF] text-[10px] font-semibold uppercase tracking-wider text-[#78736E]">
                                            Begge brands
                                        </span>
                                    ) : (
                                        brandScope.map((b) => (
                                            <span
                                                key={b}
                                                className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${BRAND_BADGE[b]?.cls || "bg-[#F0EBE1] text-[#78736E]"}`}
                                            >
                                                {BRAND_BADGE[b]?.label || b}
                                            </span>
                                        ))
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    {editable && !isEditing && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => toggleActive(item)}
                                                disabled={busyId === item.id}
                                                className="h-7 px-2 text-[#78736E] hover:bg-[#F4F2EF]"
                                                data-testid={`toggle-instruction-${item.id}`}
                                                title={item.is_active ? "Pause" : "Aktivér"}
                                            >
                                                {item.is_active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => startEdit(item)}
                                                disabled={busyId === item.id}
                                                className="h-7 px-2 text-[#78736E] hover:bg-[#F4F2EF]"
                                                data-testid={`edit-instruction-${item.id}`}
                                                title="Rediger"
                                            >
                                                <Edit3 className="w-3.5 h-3.5" />
                                            </Button>
                                        </>
                                    )}
                                    {isAdmin && !isEditing && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => remove(item.id)}
                                            disabled={busyId === item.id}
                                            className="h-7 px-2 text-red-700 hover:bg-red-50"
                                            data-testid={`delete-instruction-${item.id}`}
                                            title="Slet"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {isEditing ? (
                                <div>
                                    <Textarea
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        rows={3}
                                        maxLength={1500}
                                        className="border-[#E5E1DA] text-sm resize-y mb-2"
                                        data-testid={`edit-textarea-${item.id}`}
                                    />
                                    <div className="mb-2">
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#78736E] mb-1.5">Gælder for</div>
                                        <BrandSelector value={editBrand} onChange={setEditBrand} idPrefix={`edit-${item.id}`} disabled={busyId === item.id} />
                                    </div>
                                    <div className="mb-2">
                                        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#78736E] mb-1.5">Vedhæftede filer</div>
                                        <input
                                            ref={editFileInputRef}
                                            type="file"
                                            multiple
                                            accept="image/*,.pdf,.heic,.heif,.doc,.docx,.xls,.xlsx"
                                            className="hidden"
                                            data-testid={`edit-file-input-${item.id}`}
                                            onChange={(e) => {
                                                handleUpload(e.target.files, "edit");
                                                e.target.value = "";
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => editFileInputRef.current?.click()}
                                            disabled={editUploading || busyId === item.id}
                                            className="h-8 border-dashed text-xs"
                                            data-testid={`edit-upload-button-${item.id}`}
                                        >
                                            {editUploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5 mr-1.5" />}
                                            Vedhæft fil
                                        </Button>
                                        <AttachmentList
                                            files={editFiles}
                                            onRemove={(fid) => setEditFiles((prev) => prev.filter((f) => f.id !== fid))}
                                            disabled={busyId === item.id}
                                            idPrefix={`edit-${item.id}`}
                                        />
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={cancelEdit}
                                            className="h-8 text-[#78736E]"
                                            data-testid={`cancel-edit-${item.id}`}
                                        >
                                            <X className="w-3.5 h-3.5 mr-1" /> Annullér
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => saveEdit(item.id)}
                                            disabled={busyId === item.id || editUploading || !editText.trim()}
                                            className="h-8 bg-[#4A4641] hover:bg-[#2C2A29] text-white"
                                            data-testid={`save-edit-${item.id}`}
                                        >
                                            <Check className="w-3.5 h-3.5 mr-1" /> Gem
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="text-sm text-[#2C2A29] whitespace-pre-wrap leading-relaxed" data-testid={`instruction-text-${item.id}`}>
                                        {item.text}
                                    </div>
                                    {files.length > 0 && (
                                        <AttachmentList files={files} idPrefix={`view-${item.id}`} />
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
